import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Search, Loader2, Edit2, Save, X, Key, ToggleLeft, ToggleRight, Trash2, Building2 } from 'lucide-react';
import { authAPI, branchAPI } from '../../services/api';
import { toast } from 'sonner';


const ROLES = ['Admin', 'Manager', 'Salesman', 'Operator', 'Accounts', 'Branch Incharge', 'Logistics'];

const roleBadge = {
  Admin: 'bg-red-50 text-red-700 border-red-200',
  Manager: 'bg-blue-50 text-blue-700 border-blue-200',
  Salesman: 'bg-green-50 text-green-700 border-green-200',
  Operator: 'bg-amber-50 text-amber-700 border-amber-200',
  Accounts: 'bg-purple-50 text-purple-700 border-purple-200',
  'Branch Incharge': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Logistics: 'bg-teal-50 text-teal-700 border-teal-200',
};

const UserManagementTab = () => {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Create user
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'Salesman', branch: '', allowed_branches: [] });
  const [creating, setCreating] = useState(false);

  // Branch access
  const [accessUser, setAccessUser] = useState(null);
  const [accessBranches, setAccessBranches] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);

  // Edit user
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset password
  const [resetId, setResetId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const [usersData, branchesData] = await Promise.all([
        authAPI.listUsers(params),
        branchAPI.getAll().catch(() => []),
      ]);
      setUsers(usersData || []);
      setBranches(branchesData || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const branchMap = {};
  branches.forEach(b => { branchMap[b.id] = b.name; });

  const handleCreate = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      setCreating(true);
      await authAPI.register(createForm);
      toast.success(`User "${createForm.name}" created successfully`);
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '', role: 'Salesman', branch: '', allowed_branches: [] });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (user) => {
    setEditingId(user.id);
    setEditForm({ name: user.name, email: user.email, role: user.role, branch: user.branch || '' });
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      await authAPI.updateUser(editingId, editForm);
      toast.success('User updated');
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const result = await authAPI.toggleUserActive(user.id);
      toast.success(result.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Permanently delete "${user.name}" (${user.email})?\n\nThis action cannot be undone.`)) return;
    try {
      const result = await authAPI.deleteUser(user.id);
      toast.success(result.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const result = await authAPI.resetPassword(resetId, newPassword);
      toast.success(result.message);
      setResetId(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const openAccess = (user) => {
    setAccessUser(user);
    setAccessBranches(Array.isArray(user.allowed_branches) ? user.allowed_branches : []);
  };

  const toggleAccessBranch = (id) => {
    setAccessBranches(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
  };

  const handleSaveAccess = async () => {
    try {
      setSavingAccess(true);
      await authAPI.updateUser(accessUser.id, { allowed_branches: accessBranches });
      toast.success(accessBranches.length ? `Access restricted to ${accessBranches.length} branch(es)` : 'Access set to all branches');
      setAccessUser(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update branch access');
    } finally {
      setSavingAccess(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="user-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <input data-testid="user-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200 w-64" />
          </div>
          <select data-testid="user-role-filter" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <span className="text-[10px] text-gray-400">{users.length} users</span>
        </div>
        <button data-testid="add-user-btn" onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
          <UserPlus className="w-3.5 h-3.5" /> Add User
        </button>
      </div>

      {/* Create User Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-blue-200 p-5" data-testid="create-user-form">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Create New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Name *</label>
              <input data-testid="create-user-name" value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="Full Name" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Email *</label>
              <input data-testid="create-user-email" type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@prismoline.com" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Password *</label>
              <input data-testid="create-user-password" type="text" value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="Min 6 characters" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Role *</label>
              <select data-testid="create-user-role" value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200">
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Branch</label>
              <select data-testid="create-user-branch" value={createForm.branch} onChange={e => setCreateForm({ ...createForm, branch: e.target.value })}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200">
                <option value="">No Branch</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
          <div className="mt-4 border-t border-gray-100 pt-3" data-testid="create-user-access-section">
            <label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1">Data Access Branches</label>
            <p className="text-[10px] text-gray-400 mb-2">Select the branches this user can see (applies to ALL roles incl. Admin/Manager). Leave empty = access to all branches.</p>
            <div className="flex flex-wrap gap-2">
              {branches.map(b => {
                const on = createForm.allowed_branches.includes(b.id);
                return (
                  <button key={b.id} type="button" data-testid={`create-access-${b.id}`}
                    onClick={() => setCreateForm(f => ({ ...f, allowed_branches: on ? f.allowed_branches.filter(x => x !== b.id) : [...f.allowed_branches, b.id] }))}
                    className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                    {b.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setShowCreate(false); setCreateForm({ name: '', email: '', password: '', role: 'Salesman', branch: '', allowed_branches: [] }); }}
              className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
            <button data-testid="create-user-submit" onClick={handleCreate} disabled={creating || !createForm.name || !createForm.email || !createForm.password}
              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              {creating ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-xs text-gray-400">No users found</div>
        ) : (
          <table className="w-full text-left" data-testid="users-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/70">
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Name</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Role</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Branch</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const isEditing = editingId === user.id;
                return (
                  <tr key={user.id} className={`border-b border-gray-50 hover:bg-gray-50/50 ${!user.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                          className="px-2 py-1 text-xs border border-blue-200 rounded w-full" />
                      ) : (
                        <span className="text-xs font-semibold text-gray-800">{user.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                          className="px-2 py-1 text-xs border border-blue-200 rounded w-full" />
                      ) : (
                        <span className="text-[11px] text-gray-600">{user.email}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                          className="px-2 py-1 text-xs border border-blue-200 rounded">
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${roleBadge[user.role] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                          {user.role}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <select value={editForm.branch} onChange={e => setEditForm({ ...editForm, branch: e.target.value })}
                          className="px-2 py-1 text-xs border border-blue-200 rounded">
                          <option value="">No Branch</option>
                          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[11px] text-gray-500">{user.branch_name || branchMap[user.branch] || user.branch || '—'}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded w-fit ${(user.allowed_branches && user.allowed_branches.length) ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-400'}`}>
                            {(user.allowed_branches && user.allowed_branches.length) ? `${user.allowed_branches.length} branch access` : 'All branches'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <button onClick={handleSaveEdit} disabled={saving}
                            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50">
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                          </button>
                          <button onClick={() => setEditingId(null)}
                            className="px-2 py-1 text-[10px] text-gray-500 bg-gray-100 rounded hover:bg-gray-200">Cancel</button>
                        </div>
                      ) : (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${user.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!isEditing && (
                      <div className="flex items-center gap-1 justify-end">
                            <button data-testid={`edit-user-${user.id}`} onClick={() => startEdit(user)} title="Edit User"
                              className="p-1 text-blue-500 hover:bg-blue-50 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button data-testid={`access-user-${user.id}`} onClick={() => openAccess(user)} title="Branch Access"
                              className="p-1 text-indigo-500 hover:bg-indigo-50 rounded"><Building2 className="w-3.5 h-3.5" /></button>
                            <button data-testid={`reset-pw-${user.id}`} onClick={() => { setResetId(user.id); setNewPassword(''); }} title="Reset Password"
                              className="p-1 text-amber-500 hover:bg-amber-50 rounded"><Key className="w-3.5 h-3.5" /></button>
                            <button data-testid={`toggle-user-${user.id}`} onClick={() => handleToggleActive(user)} title={user.is_active !== false ? 'Deactivate' : 'Activate'}
                              className={`p-1 rounded ${user.is_active !== false ? 'text-red-400 hover:bg-red-50' : 'text-green-500 hover:bg-green-50'}`}>
                              {user.is_active !== false ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                            </button>
                            {user.is_active === false && (
                              <button data-testid={`delete-user-${user.id}`} onClick={() => handleDeleteUser(user)} title="Delete User (permanent)"
                                className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                      </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Reset Password Dialog */}
      {resetId && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="reset-pw-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Reset Password</h3>
            <p className="text-[11px] text-gray-500 mb-3">For: {users.find(u => u.id === resetId)?.name || ''} ({users.find(u => u.id === resetId)?.email || ''})</p>
            <input data-testid="new-password-input" type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="New password (min 6 characters)"
              className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200" autoFocus />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setResetId(null); setNewPassword(''); }}
                className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button data-testid="confirm-reset-pw" onClick={handleResetPassword} disabled={newPassword.length < 6}
                className="px-4 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-40">
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Branch Access Dialog */}
      {accessUser && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="access-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-indigo-500" />
              <h3 className="text-sm font-bold text-gray-900">Branch Data Access</h3>
            </div>
            <p className="text-[11px] text-gray-500 mb-1">For: {accessUser.name} ({accessUser.role})</p>
            <p className="text-[10px] text-gray-400 mb-3">Restrict what this user can see across Inward, Dispatch, Stock, Production & Reports. Leave all unchecked = access to <strong>all branches</strong>.</p>
            <div className="max-h-64 overflow-y-auto space-y-1.5 border border-gray-100 rounded-lg p-2">
              {branches.length === 0 ? (
                <p className="text-[11px] text-gray-400 py-4 text-center">No branches configured</p>
              ) : branches.map(b => {
                const on = accessBranches.includes(b.id);
                return (
                  <label key={b.id} data-testid={`access-branch-${b.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                    <input type="checkbox" checked={on} onChange={() => toggleAccessBranch(b.id)}
                      className="w-3.5 h-3.5 accent-indigo-600" />
                    <span className="text-xs text-gray-700">{b.name}</span>
                    {b.code && <span className="text-[10px] text-gray-400">({b.code})</span>}
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-[10px] text-gray-400">{accessBranches.length ? `${accessBranches.length} selected` : 'All branches'}</span>
              <div className="flex gap-2">
                <button onClick={() => setAccessUser(null)}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button data-testid="save-access-btn" onClick={handleSaveAccess} disabled={savingAccess}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40">
                  {savingAccess ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save Access
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTab;
